import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import NewsLetter from "../elements/NewsLetter";
import PageTitle from "../elements/PageTitle";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_URL = `${API_BASE}/courses`;

const Lessons = () => {
  const { courseId } = useParams(); // Course ID from URL
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseTitle, setCourseTitle] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    async function fetchLessons() {
      try {
        setLoading(true);
        setError(null);

        if (!courseId) {
          setError("Invalid course ID");
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_URL}/${courseId}/lessons`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to fetch lessons: ${res.statusText}`);
        }

        const data = await res.json();
        // Sort by position to ensure correct order
        const sortedLessons = data.sort((a, b) => (a.position || 0) - (b.position || 0));
        setLessons(sortedLessons);
        
        // Try to get course title from first lesson's course_id if needed
        // You might want to fetch course details separately if needed
      } catch (err) {
        console.error("Error fetching lessons:", err);
        setError(err.message || "Failed to load lessons. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchLessons();
  }, [courseId]);

  // Disable body scroll when video modal is open
  useEffect(() => {
    if (selectedLesson) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedLesson]);

  return (
    <div className="page-wraper">
      <div className="page-content" style={{ backgroundColor: "#fdffa2ff" }}>
        <PageTitle 
          activePage={courseTitle || "Lessons"} 
          parentTitle="Lessons" 
        />

        <section className="content-inner">
          <div className="container-fluid container-lg py-3 py-md-4 py-lg-5">
            {error && (
              <div className="alert alert-danger mx-2 mx-md-3" role="alert">
                {error}
              </div>
            )}

            {loading && !error && (
              <div
                className="boxes"
                style={{ margin: "40px auto", width: "96px", height: "64px" }}
              >
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            )}

            {!loading && !error && lessons.length > 0 && (
              <>
                <div className="mb-3 mb-md-4 text-center px-2">
                  <h3 className="fw-bold mb-2" style={{ fontSize: "clamp(1.25rem, 4vw, 1.75rem)" }}>
                    Course Lessons
                  </h3>
                  <p className="text-muted mb-0" style={{ fontSize: "clamp(0.875rem, 2.5vw, 1rem)" }}>
                    {lessons.length} {lessons.length === 1 ? "lesson" : "lessons"} available
                  </p>
                </div>
                
                <div className="lessons-list" style={{ maxWidth: "600px", margin: "0 auto" }}>
                  {lessons.map((lesson, index) => (
                    <div 
                      key={lesson.id}
                      className="lesson-item"
                      onClick={() => lesson.video_url && setSelectedLesson(lesson)}
                      style={{ 
                        display: "flex",
                        alignItems: "center",
                        padding: "20px 24px",
                        marginBottom: "16px",
                        minHeight: "80px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        cursor: lesson.video_url ? "pointer" : "default",
                        transition: "all 0.3s ease",
                        borderLeft: "4px solid rgb(0, 0, 0)"
                      }}
                      onMouseEnter={(e) => {
                        if (lesson.video_url) {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                          e.currentTarget.style.transform = "translateX(5px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (lesson.video_url) {
                          e.currentTarget.style.backgroundColor = "#fff";
                          e.currentTarget.style.transform = "translateX(0)";
                        }
                      }}
                    >
                      <div 
                        className="badge bg-primary me-3 flex-shrink-0"
                        style={{ 
                          fontSize: "0.9rem", 
                          minWidth: "35px", 
                          height: "35px", 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center",
                          borderRadius: "6px"
                        }}
                      >
                        {lesson.position || index + 1}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <h5 
                          className="mb-0 fw-bold"
                          style={{ 
                            fontSize: "1rem", 
                            wordBreak: "break-word",
                            color: "#333"
                          }}
                        >
                          {lesson.title}
                        </h5>
                      </div>
                    
                    </div>
                  ))}
                </div>
              </>
            )}

            {!loading && !error && lessons.length === 0 && (
              <div className="text-center py-4 py-md-5 px-2">
                <p className="text-muted mb-3" style={{ fontSize: "clamp(0.875rem, 2.5vw, 1rem)" }}>
                  No lessons available for this course.
                </p>
                <Link 
                  to="/Categories" 
                  className="btn btn-dark mt-2"
                  style={{ 
                    fontSize: "clamp(0.875rem, 2.5vw, 1rem)",
                    padding: "0.5rem 1.5rem"
                  }}
                >
                  Browse Categories
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="call-action style-1 footer-action">
          <div className="container">
            <NewsLetter />
          </div>
        </section>
      </div>

      {/* Video Modal */}
      {selectedLesson && (
        <div 
          className="video-modal"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            background: "rgba(0, 0, 0, 0.9)",
            padding: "8px",
            overflowY: "auto",
          }}
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.pause();
            }
            setSelectedLesson(null);
          }}
        >
          <div 
            className="video-wrapper"
            style={{
              position: "relative",
              zIndex: 10000,
              maxWidth: "90%",
              maxHeight: "95vh",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
              borderRadius: "12px",
              overflow: "hidden",
              margin: "20px auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.pause();
                }
                setSelectedLesson(null);
              }}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "rgba(0, 0, 0, 0.7)",
                border: "none",
                borderRadius: "50%",
                width: "clamp(32px, 6vw, 40px)",
                height: "clamp(32px, 6vw, 40px)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "clamp(18px, 4vw, 24px)",
                color: "#fff",
                zIndex: 10001,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.9)";
                e.target.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.7)";
                e.target.style.transform = "scale(1)";
              }}
              aria-label="Close video"
            >
              ×
            </button>
            
            {/* Title Section - Top on mobile only */}
            <div 
              className="video-title-section"
              style={{ 
                padding: "clamp(1rem, 3vw, 2rem)",
                paddingBottom: "0.5rem",
                order: 1
              }}
            >
              <h4 
                className="mb-0 fw-bold"
                style={{ 
                  fontSize: "clamp(1.1rem, 3vw, 1.5rem)",
                  color: "#222"
                }}
              >
                {selectedLesson.title}
              </h4>
            </div>
            
            {/* Video Section - Middle on mobile, right side on tablet/web */}
            {selectedLesson.video_url && (
              <div 
                className="video-section"
                style={{ 
                  width: "100%", 
                  backgroundColor: "#000",
                  order: 2
                }}
              >
                <video
                  ref={videoRef}
                  src={selectedLesson.video_url}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
            
            {/* Content Section - Bottom on mobile, left side on tablet/web (includes title) */}
            <div 
              className="video-content-section"
              style={{ 
                padding: "clamp(1rem, 3vw, 2rem)",
                overflowY: "auto",
                order: 3
              }}
            >
              {/* Title for tablet/web - hidden on mobile */}
              <h4 
                className="video-content-title mb-3 fw-bold"
                style={{ 
                  fontSize: "clamp(1.1rem, 3vw, 1.5rem)",
                  color: "#222",
                  display: "none"
                }}
              >
                {selectedLesson.title}
              </h4>
              {selectedLesson.content && (
                <div 
                  style={{ 
                    fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
                    lineHeight: "1.6",
                    color: "#555",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  {selectedLesson.content}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Responsive Styles */}
      <style>{`
        /* Mobile devices */
        @media (max-width: 575px) {
          .lesson-badge {
            min-width: 40px !important;
            height: 40px !important;
            font-size: 0.9rem !important;
          }
          
          .card-body {
            padding: 0.75rem !important;
          }
          
          .video-modal {
            padding: 4px !important;
          }
          
          .video-wrapper {
            max-width: 98% !important;
            max-height: 98vh !important;
          }
        }
        
        /* Small tablets */
        @media (min-width: 576px) and (max-width: 767px) {
          .lesson-badge {
            min-width: 45px !important;
            height: 45px !important;
          }
          
          .card-body {
            padding: 1rem !important;
          }
        }
        
        /* Tablets */
        @media (min-width: 768px) and (max-width: 991px) {
          .card-body {
            padding: 1.25rem !important;
          }
        }
        
        /* Desktop and larger */
        @media (min-width: 992px) {
          .lesson-card {
            transition: all 0.3s ease;
          }
          
          .lesson-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
          }
          
          .card-body {
            padding: 1.5rem !important;
          }
        }
        
        /* Make cards with video look clickable */
        .lesson-card[style*="cursor: pointer"]:hover {
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2) !important;
        }
        
        @media (max-width: 991px) {
          .lesson-card[style*="cursor: pointer"]:active {
            transform: scale(0.98);
          }
        }
        
        /* Desktop - ensure cards don't stretch too wide */
        @media (min-width: 992px) {
          .lesson-card {
            max-width: 100%;
          }
        }
        
        /* Large desktop - better spacing */
        @media (min-width: 1200px) {
          .row {
            max-width: 1400px;
            margin-left: auto;
            margin-right: auto;
            padding-left: 15px;
            padding-right: 15px;
          }
        }
        
        /* Extra large desktop */
        @media (min-width: 1400px) {
          .row {
            max-width: 1600px;
          }
          
          .lesson-card {
            max-width: 100%;
          }
        }
        
        /* Ensure proper card width on all screens */
        .lesson-card {
          width: 100%;
          max-width: 100%;
        }
        
        /* Video Modal - Mobile (including 375x667): title top, video middle, content bottom */
        @media (max-width: 539px) {
          .video-wrapper {
            flex-direction: column !important;
            max-height: 95vh !important;
          }
          
          .video-title-section {
            order: 1 !important;
            display: block !important;
            flex-shrink: 0 !important;
            padding: 1rem 1.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          
          .video-section {
            order: 2 !important;
            flex-shrink: 0 !important;
            max-height: 70vh !important;
            overflow: hidden !important;
          }
          
          .video-section video {
            max-height: 70vh !important;
            width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
          }
          
          .video-content-section {
            order: 3 !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            max-height: none !important;
            padding: 1rem 1.5rem !important;
            padding-top: 0.75rem !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          .video-content-title {
            display: none !important;
          }
        }
        
        /* Video Modal - Tablet and Web (540px+): content left (with title), video right */
        @media (min-width: 540px) {
          .video-wrapper {
            flex-direction: row !important;
            max-width: 95% !important;
            max-height: 90vh !important;
          }
          
          .video-title-section {
            display: none !important;
          }
          
          .video-content-section {
            order: 1 !important;
            flex: 0 0 40% !important;
            max-height: 90vh !important;
            border-right: 1px solid #e0e0e0;
            overflow-y: auto !important;
          }
          
          .video-content-title {
            display: block !important;
          }
          
          .video-section {
            order: 2 !important;
            flex: 0 0 60% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            max-height: 90vh !important;
            overflow: hidden !important;
          }
          
          .video-section video {
            max-height: 90vh !important;
            width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
          }
        }
        
        /* Small tablets and Surface Duo - adjust proportions */
        @media (min-width: 540px) and (max-width: 767px) {
          .video-content-section {
            flex: 0 0 45% !important;
          }
          
          .video-section {
            flex: 0 0 55% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Lessons;
